import { Badge, Button, Card, Checkbox, Grid, Space, Tabs, Typography, message } from "antd";
import {
	ArticleCommentProperties,
	ArticleProperties,
	EArticleCommentStatus,
	ListCommentQuery,
} from "../../../../types/blog.type";
import { ReactComponent as SortIcon } from "../../../../assets/Icons/Sort.svg";
import styled from "styled-components";
import ListComment2 from "./ListComment2";
import { PlusOutlined } from "@ant-design/icons";
import ModalAddCommentOrReply from "./ModalAddCommentOrReply";
import { useEffect, useState } from "react";
import { httpRequest } from "../../../../helpers/api";
import useFetchList from "../../../../hooks/useFetchList";
import { getErrorMessage } from "../../../../helpers/errorHandler";
import { BaseResponseProps } from "../../../../types/config.type";
import { generateQueryString } from "../../../../helpers/generateQueryString";

const { Text } = Typography;

export default function CommentSection({
	post,
	setIsFetch,
}: {
	post: ArticleProperties;
	setIsFetch?: React.Dispatch<React.SetStateAction<boolean>>;
}) {
	const { xs } = Grid.useBreakpoint();
	const [filterType, setFilterType] = useState("oldest");

	const [showModalAdd, setShowModalAdd] = useState(false);
	const [showHideContent, setShowHideContent] = useState(false);
	const [commentStatistic, setCommentStatistic] = useState<{
		totalComment: number;
		totalCommentWaitingApproval: number;
		totalCommentApprove: number;
		totalCommentReject: number;
	}>({
		totalComment: 0,
		totalCommentWaitingApproval: 0,
		totalCommentApprove: 0,
		totalCommentReject: 0,
	});
	const [isFetchStatistic, setIsFetchStatistic] = useState(true);

	const {
		isLoading,
		data: comments,
		fetchList,
		setQuery,
		query,
	} = useFetchList<ArticleCommentProperties, ListCommentQuery>({
		apiRequest: httpRequest,
		endpoint: "article-comments",
		initialQuery: {
			sort: "createdAt:ASC",
			articleIds: post?.articleId,
			status: EArticleCommentStatus.WAITING_APPROVAL,
		},
	});

	useEffect(() => {
		if (isFetchStatistic) {
			(async () => {
				try {
					const res = await httpRequest.get<
						BaseResponseProps<{
							totalComment: number;
							totalCommentWaitingApproval: number;
							totalCommentApprove: number;
							totalCommentReject: number;
						}>
					>(
						`/article-comments/custom/comment-statistic${generateQueryString({
							articleIds: post?.articleId,
						})}`
					);

					setCommentStatistic(res.data.payload);
				} catch (error) {
					message.error(getErrorMessage(error));
				}
			})();

			setIsFetchStatistic(false);
		}
	}, [post?.articleId, isFetchStatistic]);

	useEffect(() => {
		if (filterType === "latest") {
			setQuery({
				...query,
				sort: "createdAt:DESC",
			});
		}

		if (filterType === "oldest") {
			setQuery({
				...query,
				sort: "createdAt:ASC",
			});
		}
	}, [filterType]);
	return (
		<Card
			bordered={false}
			size="small"
			loading={isLoading}
		>
			<Space
				direction="vertical"
				size={16}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
					}}
				>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => {
							setShowModalAdd(true);
						}}
					>
						Add Comment
					</Button>
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 12,
					}}
				>
					<Space>
						<Typography.Title
							level={3}
							style={{
								fontSize: 20,
								margin: 0,
							}}
						>
							Comments
						</Typography.Title>
						<Badge
							count={commentStatistic.totalComment}
							style={{
								backgroundColor: "red",
								color: "white",
								fontSize: xs ? "12px" : "16px",
								height: xs ? 24 : 28,
								width: xs ? 24 : 28,
								borderRadius: 999,
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						/>
					</Space>

					<Space>
						<BtnOutlined
							type="default"
							onClick={() => {
								setFilterType(filterType === "oldest" ? "latest" : "oldest");
							}}
						>
							<Space>
								<span>{filterType === "oldest" ? "Latest" : "Oldest"} Comment</span>
								<SortIcon />
							</Space>
						</BtnOutlined>
					</Space>
				</div>

				<div>
					<CustomTabs
						className="red-tabs"
						renderTabBar={(props, TabNavList) => (
							<TabNavList
								{...props}
								mobile={true}
							/>
						)}
						activeKey={query.status}
						onChange={(v) => {
							setQuery((prev) => ({
								...prev,
								offset: 0,
								status: v,
							}));
						}}
						items={[
							{
								key: EArticleCommentStatus.WAITING_APPROVAL,
								label: (
									<>
										<Badge
											count={commentStatistic.totalCommentWaitingApproval}
											offset={[12, 0]}
										>
											<Typography.Text>Waiting Approval</Typography.Text>
										</Badge>
									</>
								),
							},
							{
								key: EArticleCommentStatus.PUBLISH,
								label: (
									<>
										<Badge
											count={commentStatistic.totalCommentApprove}
											offset={[12, 0]}
										>
											<Typography.Text>Approved</Typography.Text>
										</Badge>
									</>
								),
							},
							{
								key: EArticleCommentStatus.REJECTED,
								label: (
									<>
										<Badge
											count={commentStatistic.totalCommentReject}
											offset={[12, 0]}
										>
											<Typography.Text>Rejected</Typography.Text>
										</Badge>
									</>
								),
							},
						]}
					/>

					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
						}}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<Checkbox
								value={showHideContent}
								checked={showHideContent}
								onChange={(e) => {
									setShowHideContent(e.target.checked);
								}}
							/>
							<Typography.Text
								style={{
									fontSize: 12,
								}}
							>
								Show Rejected/Deleted
							</Typography.Text>
						</div>
					</div>
				</div>

				<div>
					{comments?.length === 0 ? (
						<Space
							direction="horizontal"
							style={{
								width: "100%",
								display: "flex",
								justifyContent: "center",
								marginBlock: 16,
							}}
						>
							<Text
								type="secondary"
								style={{ fontSize: 16 }}
							>
								Belum ada komentar
							</Text>
						</Space>
					) : (
						<>
							<ListComment2
								comments={comments}
								// setIsFetch={setIsFetch}
								showHideContent={showHideContent}
								articleId={post?.articleId}
								onSuccess={() => {
									fetchList();
									setIsFetchStatistic(true);
								}}
							/>
						</>
					)}
				</div>
			</Space>

			<ModalAddCommentOrReply
				open={showModalAdd}
				articleId={post?.articleId}
				type="comment"
				onClose={() => {
					setShowModalAdd(false);
				}}
				onSuccess={() => {
					fetchList();
					setIsFetchStatistic(true);
				}}
			/>
		</Card>
	);
}

const BtnOutlined = styled(Button)`
	background-color: transparent;
	border: 1px solid #00133b;
	color: #00133b;
`;

const ReplyIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M2.84192 9.49715H12.3334V1.49715H1.66671V10.4206L2.84192 9.49715ZM3.30307 10.8305L0.333374 13.1638V0.830485C0.333374 0.462298 0.631854 0.163818 1.00004 0.163818H13C13.3682 0.163818 13.6667 0.462298 13.6667 0.830485V10.1638C13.6667 10.532 13.3682 10.8305 13 10.8305H3.30307Z"
			fill="black"
		/>
	</svg>
);

const CustomTabs = styled(Tabs)`
	.ant-tabs-tab.ant-tabs-tab-active {
		.ant-badge .ant-typography {
			color: #ff4d4f;
		}
	}
`;
